import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import { store } from '@/store/index';
import {
  readAssertedCompanyId,
  readAssertedDepartments,
  readSwitchableCompanies,
  readAssertedUserProfile,
  readForceEnroll2Fa,
} from '@/store/main/getters';
import {
  dispatchGetCompanyDepartments,
  dispatchGetCurrentUser,
  dispatchUpdateCurrentUser,
} from '@/store/main/actionsMain';
import UserProfileEditForm from '@/views/pages/user/UserProfileEditForm.vue';
import BaseButtonSubmit from '@/views/base/BaseButtonSubmit.vue';
import sharedMessages from '@/lib/shared-messages';
import { defineMessages } from '@/lib/i18n';
import TwoFactorSettings from '@/views/components/views/twoFactorAuth/TwoFactorSettings.vue';
import { Department } from '@/interfaces/model/general/department';
import { useDepartmentComposable } from '@/composables/useDepartment';


export default /*#__PURE__*/_defineComponent({
  __name: 'UserPage',
  setup(__props) {

const m = defineMessages({
  forceEnroll2FaNotice:
    '管理者により二要素認証の設定が必須化されています。設定を完了するまで、他の機能は利用できません。',
});

const forceEnroll2Fa = computed(() => readForceEnroll2Fa(store));
const isValidForm = ref(false);
const isLoading = ref(false);
const selectableDepartments = computed(() => readAssertedDepartments(store));
const { departmentTree, editableDepartmentTree, getCompanyDepartmentTree } =
  useDepartmentComposable();

// Get the user's actual company (not the currently displayed one)
const companyName = computed(() => {
  const userProfile = readAssertedUserProfile(store);
  const userCompanyId = userProfile.company_id;
  const switchableCompanies = readSwitchableCompanies(store);

  // Find the company in switchable companies list
  const userCompany = switchableCompanies.find(
    (company) => company.uuid === userCompanyId
  );

  return userCompany?.name || '';
});

// Create refs for all editable user fields
const userProfile = computed(() => readAssertedUserProfile(store));
const user = ref({
  email: userProfile.value.email,
  first_name: userProfile.value.first_name,
  last_name: userProfile.value.last_name,
  middle_name: userProfile.value.middle_name,
  phone: userProfile.value.phone,
  position: userProfile.value.position,
  is_subscribe: userProfile.value.is_subscribe,
  is_checklist_notify_subscribe:
    userProfile.value.is_checklist_notify_subscribe ?? true,
  role: userProfile.value.role,
  departments: [] as Department[],
});

// Local, editable mirror of the current company's departments. Reading
// straight from the store here (instead of `user.value.departments`) would
// discard every local edit on the next render, since the store only changes
// after a successful save - chips would never appear and removing one would
// appear to do nothing.
const displayedDepartments = computed({
  get: () => user.value.departments,
  set: (newDepartments: Department[]) => {
    user.value.departments = newDepartments;
  },
});

// Reset the local department selection to what the currently displayed
// company actually has, discarding any uncommitted local edit.
const syncDisplayedDepartments = () => {
  const currentCompanyId = readAssertedCompanyId(store);
  const allDepartments = userProfile.value.departments || [];
  user.value.departments = allDepartments.filter(
    (d) => d.company_id === currentCompanyId
  );
};

// Watch for changes in userProfile and update local user ref
watch(
  userProfile,
  (newProfile) => {
    user.value.email = newProfile.email;
    user.value.first_name = newProfile.first_name;
    user.value.last_name = newProfile.last_name;
    user.value.middle_name = newProfile.middle_name;
    user.value.phone = newProfile.phone;
    user.value.position = newProfile.position;
    user.value.is_subscribe = newProfile.is_subscribe;
    user.value.is_checklist_notify_subscribe =
      newProfile.is_checklist_notify_subscribe ?? true;
    user.value.role = newProfile.role;
    syncDisplayedDepartments();
  },
  { deep: true, immediate: true }
);

// Watch for company changes to reload departments
watch(
  () => readAssertedCompanyId(store),
  async (newCompanyId) => {
    if (newCompanyId) {
      syncDisplayedDepartments();
      await dispatchGetCompanyDepartments(store, {
        companyId: newCompanyId,
      });
      await getCompanyDepartmentTree();
    }
  }
);

const updateCurrentUser = async (): Promise<void> => {
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;
  try {
    const currentCompanyId = readAssertedCompanyId(store);

    await dispatchUpdateCurrentUser(store, {
      first_name: user.value.first_name,
      last_name: user.value.last_name,
      middle_name: user.value.middle_name,
      phone: user.value.phone,
      position: user.value.position,
      is_subscribe: user.value.is_subscribe,
      is_checklist_notify_subscribe: user.value.is_checklist_notify_subscribe,
      departments: user.value.departments.map((d) => d.uuid),
      company_id: currentCompanyId,
    });
    await dispatchGetCurrentUser(store);
  } catch (e) {
    console.log(e);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async (): Promise<void> => {
  await dispatchGetCurrentUser(store);
  await dispatchGetCompanyDepartments(store, {
    companyId: readAssertedCompanyId(store),
  });
  await getCompanyDepartmentTree();
});

return { __sfc: true,m, forceEnroll2Fa, isValidForm, isLoading, selectableDepartments, departmentTree, editableDepartmentTree, getCompanyDepartmentTree, companyName, userProfile, user, displayedDepartments, syncDisplayedDepartments, updateCurrentUser, UserProfileEditForm, BaseButtonSubmit, sharedMessages, TwoFactorSettings }
}

})